import { castArray, compact, isEmpty } from 'lodash';
import GenericIndexedDb from 'src/indexed-db/generic-indexedDb';
import { createEncryptionKeys, decryptPrivateKey, readSecret } from 'src/utils/CryptoUtils';

export const BACKUP_DATA = 'backupData';
export const BACKUP_NOTES = 'backupNotes';

/**
 * Clear all backups
 */
export async function clearBackups() {
  const sessionBackupDataDb = new GenericIndexedDb(BACKUP_DATA);
  const sessionBackupNotesDb = new GenericIndexedDb(BACKUP_NOTES);
  await sessionBackupDataDb.clearTable();
  await sessionBackupNotesDb.clearTable();
} // clearBackups()

/**
 * Clear client backup data
 *
 * @param {string} clientId
 */
export async function clearClientBackupData(clientId) {
  const sessionBackupDataDb = new GenericIndexedDb(BACKUP_DATA);

  const clientBackupData = await sessionBackupDataDb.readAll(
    [['client', '==', clientId]],
  );

  if (isEmpty(clientBackupData)) {
    return;
  }

  await Promise.all(clientBackupData.map((doc) => {
    return sessionBackupDataDb.delete(`backupData_${doc.id}`, true);
  }));
} // clearClientBackupData()

/**
 * Clear client backup notes
 *
 * @param {string} clientId
 */
export async function clearClientBackupNotes(clientId) {
  const sessionBackupNotesDb = new GenericIndexedDb(BACKUP_NOTES);

  const clientBackupNotes = await sessionBackupNotesDb.readAll(
    [['client', '==', clientId]],
  );

  if (isEmpty(sessionBackupNotesDb)) {
    return;
  }

  await Promise.all(clientBackupNotes.map((doc) => {
    return sessionBackupNotesDb.delete(`backupNotes_${doc.id}`, true);
  }));
} // clearClientBackupNotes()

async function runBackupsCleanup(sessionIds) {
  const keptSessionIds = compact(castArray(sessionIds));
  if (isEmpty(keptSessionIds)) {
    // `not-in` with an empty list matches every document, including the running session's backups
    return;
  }

  const sessionBackupDataDb = new GenericIndexedDb(BACKUP_DATA);
  const sessionBackupNotesDb = new GenericIndexedDb(BACKUP_NOTES);
  // `session` is indexed in both tables, so the documents to drop are read by index instead of
  // reading (and decrypting) every backup at the moment the device is already out of space
  const constraints = [['session', 'not-in', keptSessionIds]];

  // the goal is to free up as much space as possible, so a failure on one side
  // (e.g. a backup note that cannot be decrypted) must not stop the other one
  const [dataResult, notesResult] = await Promise.allSettled([
    sessionBackupDataDb.readAll(constraints),
    sessionBackupNotesDb.readAll(constraints),
  ]);

  await Promise.allSettled([
    ...(dataResult.value || []).map((doc) => sessionBackupDataDb.delete(`backupData_${doc.id}`, true)),
    ...(notesResult.value || []).map((doc) => sessionBackupNotesDb.delete(`backupNotes_${doc.id}`, true)),
  ]);
} // runBackupsCleanup()

let pendingBackupsCleanup = null;

/**
 * Drop backups that do not belong to the given session(s) to free up storage space.
 * Concurrent calls share one cleanup - quota errors arrive in bursts (one per parallel write),
 * and running the same cleanup N times would only add pressure.
 *
 * @param {string | string[]} sessionIds session(s) whose backups must be kept
 */
export function clearBackupsExceptCurrentSession(sessionIds) {
  pendingBackupsCleanup = pendingBackupsCleanup || runBackupsCleanup(sessionIds)
    .finally(() => { pendingBackupsCleanup = null; });

  return pendingBackupsCleanup;
} // clearBackupsExceptCurrentSession()

/**
 * @param {string} encryptionPassword
 * @param {boolean} override
 */
export async function addBackupDecryptionKey(encryptionPassword, override = false) {
  if (!override && await readSecret('backupDecryptionKey')) {
    return;
  }

  await createEncryptionKeys(encryptionPassword, {
    publicKeyName: 'backupPublicKey',
    saltName: 'backupSalt',
    saltType: 'backupIv',
    privateKeyName: 'backupPrivateKey',
  });
  await decryptPrivateKey(encryptionPassword, {
    publicKeyName: 'backupPublicKey',
    saltName: 'backupSalt',
    saltType: 'backupIv',
    privateKeyName: 'backupPrivateKey',
    decryptionKeyName: 'backupDecryptionKey',
  });
} // addBackupDecryptionKey()
